import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { Location, LocationDialogData, LocationInput, LocationServiceProvider, LocationServiceProviderInput, Segment, ServiceProviderOrderByEnum, User } from '@core/interfaces';
import { getErrorCode } from '@core/utils/get-error-code';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { CoordsValidation } from '@validators/coords-validation';
import { DocumentNumberValidation } from '@validators/document-number.validation';
import { map } from 'lodash';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { sentry } from 'sentry';
import { VideoInfoModalService } from '../../../../core/components/video-info-modal/video-info-modal.service';
import { SECTOR_AND_ENVIRONMENT_ROLLOVER_DATE } from '../../../../core/consts';
import english from '../../i18n/en.json';
import spanish from '../../i18n/es.json';
import portuguese from '../../i18n/pt.json';
import { LocationService } from '../../services/location.service';
import { DialogAlertComponent } from '../dialog-alert/dialog-alert.component';

@Component({
  selector: 'dialog-locations',
  templateUrl: './dialog-locations.component.html',
  styleUrls: ['./dialog-locations.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogLocationsComponent implements OnInit, OnDestroy {
  isUpdate = false;
  isLoading = false;
  isViewMode = false;

  location: Location;
  locationForm: FormGroup;
  authenticatedUser: User;
  selectedTabIndex = 0;
  serviceProvidersSearchPerformed = false;
  serviceProvidersArray: LocationServiceProvider[] = [];
  loadingProviders = false;

  hasEditGroupPermission = false;
  hasLocationsPermissionsEnabled = true;
  sectorAndEnvironmentRollOverDate = SECTOR_AND_ENVIRONMENT_ROLLOVER_DATE;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LocationDialogData,
    private readonly matDialogRef: MatDialogRef<DialogLocationsComponent>,
    private readonly fuseProgressBarService: FuseProgressBarService,
    private readonly authenticationService: AuthenticationService,
    private readonly customMessageService: CustomMessagesService,
    private readonly locationsService: LocationService,
    private readonly formBuilder: FormBuilder,
    private readonly matDialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly translationLoader: FuseTranslationLoaderService,
    private readonly videoInfoModal: VideoInfoModalService
  ) {
    this.location = this.data.location;
    this.isViewMode = this.data.isViewMode;
    this.translationLoader.loadJsonTranslations({english, portuguese, spanish});
  }

  ngOnInit(): void {
    this.hasEditGroupPermission = this.authenticationService.hasPermission('groups-edit');
    this.hasLocationsPermissionsEnabled = this.authenticationService.hasAccountPreference({
      defaultIfUndefined: true,
      expectedValue: true,
      property: 'other',
      subProperty: 'featureLocationsPermissionsEnabled'
    });

    this.isUpdate = (this.location && this.location.id) ? true : false;

    this.locationForm = this.formBuilder.group({
      name: [this.location && this.location.name, [Validators.required, Validators.maxLength(100)]],
      locationGroup: [this.location && this.location.group, [Validators.required]],
      documentNumber: [(this.location && this.location.documentNumber) || '', [Validators.maxLength(14)]],
      address: this.formBuilder.group({
        countryCode: [null],
        postalCode: [(this.location && this.location.address && this.location.address.postalCode) || '', [Validators.maxLength(8)]],
        city: [this.location && this.location.address && this.location.address.city, [Validators.required]],
        state: [this.location && this.location.address && this.location.address.state, [Validators.required]],
        streetName: [(this.location && this.location.address && this.location.address.streetName)],
        number: [this.location && this.location.address && this.location.address.number],
        neighborhood: [this.location && this.location.address && this.location.address.neighborhood],
        formattedAddress: (this.location && this.location.address && this.location.address.formattedAddress),
        complement: (this.location && this.location.address && this.location.address.complement),
        coords: this.formBuilder.group({
          latitude: [this.location && this.location.address && this.location.address.coords && this.location.address.coords.latitude, [Validators.required, CoordsValidation.validate]],
          longitude: [this.location && this.location.address && this.location.address.coords && this.location.address.coords.longitude, [Validators.required, CoordsValidation.validate]]
        }),
      }),
      notes: (this.location && this.location.notes),
      segmentsId: [this.location && this.location.segments && this.location.segments.length ? this.location.segments : null],
      contact: this.formBuilder.group({
        emails: this.formBuilder.array((this.location && this.location.contact) && this.location.contact.emails ? this.location.contact.emails : []),
        phones: this.formBuilder.array((this.location && this.location.contact) && this.location.contact.phones ? this.location.contact.phones : [])
      }),
      serviceProviders: this.formBuilder.array([]),
      searchingWithGoogle: [true]
    });

    if (this.isViewMode) {
      this.locationForm.disable();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async onTabChange(event: MatTabChangeEvent): Promise<void> {
    const tabCompaniesName = this.translate.instant('LOCATIONS.DIALOG.TABS.COMPANIES');

    if (event.tab.textLabel === tabCompaniesName && !this.serviceProvidersSearchPerformed) {
      await this.updateServiceProvidersFormArray();
      this.serviceProvidersSearchPerformed = true;
    }
  }

  private async fetchLocationServiceProviders(locationId: string): Promise<LocationServiceProvider[]> {
    if (!locationId) {
      return null;
    }

    const limit = 100;
    let offset = 0;
    let hasNextPage = true;
    let serviceProvidersArray: LocationServiceProvider[] = [];
    this.loadingProviders = true;

    while (hasNextPage) {
      const response = await this.locationsService.getLocationServiceProviders({ locationId, limit, offset, orderBy: ServiceProviderOrderByEnum.segmentName_asc });

      const pageInfo = response.serviceProvidersConnection.pageInfo;
      const newServiceProviders = response.serviceProvidersConnection.items;

      serviceProvidersArray = serviceProvidersArray.concat(newServiceProviders);
      hasNextPage = pageInfo.hasNextPage;
      offset += limit;
    }

    this.loadingProviders = false;
    return serviceProvidersArray;
  }

  private async updateServiceProvidersFormArray(): Promise<void> {
    this.serviceProvidersArray = await this.fetchLocationServiceProviders(this.location.id);

    const serviceProvidersFormArray = this.locationForm.get('serviceProviders') as FormArray;

    serviceProvidersFormArray.clear();

    const serviceProviders = this.setServiceProvidersForm();
    serviceProviders.forEach((provider) => serviceProvidersFormArray.push(provider));
  }

  private setServiceProvidersForm(): FormGroup[] {
    return this.serviceProvidersArray.map((serviceProvider) => this.formBuilder.group({
      segment: [serviceProvider.segment, [Validators.required]],
      company: [serviceProvider.company],
      maintenanceTypes: [serviceProvider.maintenancesTypes || []],
      deadlineContract: [serviceProvider.deadlineContract],
    }));
  }

  private setServiceProvidersInput(serviceProviders: Record<string, any>[]): LocationServiceProviderInput[] {
    if (!this.serviceProvidersSearchPerformed) { return null; }
    if (!this.location) { return null; }

    return serviceProviders.map((serviceProvider: Record<string, any>) => ({
      segmentId: serviceProvider.segment && serviceProvider.segment.id,
      companyId: serviceProvider.company && serviceProvider.company.id,
      deadlineContractId: serviceProvider.deadlineContract && serviceProvider.deadlineContract.id,
      maintenanceTypeIds: map(serviceProvider.maintenanceTypes, 'id')
    }));
  }

  private getInputToSave(): LocationInput {
    const locationFormValue = this.locationForm.getRawValue();

    const emails = locationFormValue.contact.emails
      .map((contactEmail: Record<string, any>) => ({
        name: contactEmail.name,
        emailAddress: contactEmail.emailAddress,
        primary: contactEmail.primary,
        contactType: contactEmail.contactType
      }));

    const phones = locationFormValue.contact.phones
      .map((contactPhone: Record<string, any>) => ({
        name: contactPhone.name,
        number: contactPhone.number.replace(/[^0-9]/g, ''),
        primary: contactPhone.primary,
        contactType: contactPhone.contactType
      }));

    const segmentIdsArray = locationFormValue.segmentsId ? locationFormValue.segmentsId.map((segment: Segment) => segment.id)
    : this.isUpdate ? [] : null;

    const address = locationFormValue.address;

    delete address.formattedAddress;
    delete address.countryCode;

    address.postalCode = (address.postalCode || '').replace(/[^0-9]/g, '');
    address.streetName = address.streetName || '';

    return {
      address,
      serviceProviders: this.setServiceProvidersInput(locationFormValue.serviceProviders),
      documentNumber: locationFormValue.documentNumber || null,
      name: locationFormValue.name,
      group: locationFormValue.locationGroup && locationFormValue.locationGroup.id,
      segments: segmentIdsArray,
      notes: locationFormValue.notes,
      contact: { emails, phones },
      archived: this.location && this.location.archived || false
    };
  }

  public async saveLocation({ closeDialog }): Promise<boolean> {
    const locationInput = this.getInputToSave();

    this.fuseProgressBarService.show();
    this.isLoading = true;
    let success = false;

    try {
      this.location = await this.locationsService.save(this.location && this.location.id, locationInput);

      if (closeDialog) {
        this.matDialogRef.close({ reload: true, location: this.location });
        this.customMessageService.success('LOCATIONS.SUCCESS_MESSAGE');
      }

      success = true;
    } catch (err) {
      success = false;
      const code = getErrorCode(err);
      if (code === 'location_name_duplicated') {
        this.locationForm.get('name').setErrors({
          'location_name_duplicated': true
        });

        return;
      }

      if (code === 'location_document_number_duplicated') {
        this.locationForm.get('documentNumber').setErrors({
          'document_number_name_duplicated': true
        });

        return;
      }

      sentry.captureException(err);
      this.customMessageService.error('LOCATIONS.ERROR_MESSAGE');
    } finally {
      this.isLoading = false;
      this.fuseProgressBarService.hide();
    }

    return success;
  }

  public async archiveLocation(): Promise<void> {
    const alertDialog = this.matDialog.open(DialogAlertComponent, {
      panelClass: 'dialog-alert'
    });

    const archived = await alertDialog.afterClosed().toPromise();
    if (!archived) {
      return;
    }

    const location = await this.locationsService.archiveLocation(this.location.id);
    this.matDialogRef.close({ reload: true, location });
    this.customMessageService.success('LOCATIONS.SUCCESS_MESSAGE');
  }

  public async unarchiveLocation(): Promise<void> {
    const location = await this.locationsService.unarchiveLocation(this.location.id);
    this.matDialogRef.close({ reload: true, location });
    this.customMessageService.success('LOCATIONS.SUCCESS_MESSAGE');
  }

  async continue(): Promise<void> {
    if (this.selectedTabIndex !== 0) {
      this.selectedTabIndex++;
      return;
    }

    const savedSuccessfully = await this.saveLocation({ closeDialog: false });
    if (savedSuccessfully) {
      this.selectedTabIndex = 2;
    }
  }

  private openIntroVideoAndGotoLocationEnvironmentTab(): void {

    const label = document.querySelector('#links-tab-label') as HTMLSpanElement;
    if (label) { label.click(); }


    this.videoInfoModal.open({
      title: this.translate.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.INTRO_VIDEO.TITLE'),
      description: this.translate.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.INTRO_VIDEO.DESCRIPTION'),
      videoId: 'VQE_FyV8Gf0'
    });
  }
}
